import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Calendar, 
  BookOpen, 
  Users, 
  Award,
  Globe,
  Newspaper
} from 'lucide-react';

const MemberBenefits = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const benefits = [
    {
      icon: Calendar,
      title: "Annual Conference & Events",
      description: "Access to our flagship annual conference and regional seminars connecting you with industry leaders"
    },
    {
      icon: BookOpen,
      title: "Research & Publications",
      description: "Exclusive industry reports, market analysis, and the IPMI Journal delivered to your inbox"
    },
    {
      icon: Users,
      title: "Members Directory & Buyers Guide",
      description: "Online and printed directory of member contacts, plus an alphabetical guide to member products and services"
    },
    {
      icon: Award,
      title: "Awards & Recognition",
      description: "Eligibility for prestigious industry awards honoring excellence and innovation"
    },
    {
      icon: Globe,
      title: "Global Advocacy",
      description: "Representation on regulatory matters and industry standards that affect your business"
    },
    {
      icon: Newspaper,
      title: "Industry Updates",
      description: "Monthly newsletters and alerts keeping you informed of market trends and news"
    }
  ];

  return (
    <section className="member-benefits-section" ref={sectionRef}>
      {/* Watermark */}
      <div className="benefits-watermark">
        <img 
          src="https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/o4ewfjxj_IPMI-50th-Emblem.png" 
          alt="" 
          aria-hidden="true"
        />
      </div>
      
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="section-header centered"
        >
          <span className="section-eyebrow">Membership</span>
          <h2 className="section-title">What Members Receive</h2>
          <p className="section-description-medium">
            Join a global community of precious metals professionals and unlock exclusive benefits
          </p>
        </motion.div>

        <motion.div
          className="benefits-grid"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="benefit-card-large"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.4 + (index * 0.1)
                }}
                whileHover={{ y: -5 }}
              >
                <div className="benefit-icon-large">
                  <IconComponent size={28} />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Social Proof */}
        <motion.div
          className="social-proof-section"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <p className="proof-label">Trusted by industry leaders including</p>
          <div className="proof-logos">
            <span className="proof-company">Refiners</span>
            <span className="proof-divider">•</span>
            <span className="proof-company">Mints</span>
            <span className="proof-divider">•</span>
            <span className="proof-company">Banks</span>
            <span className="proof-divider">•</span>
            <span className="proof-company">Mining Companies</span>
            <span className="proof-divider">•</span>
            <span className="proof-company">Technology Firms</span>
          </div>
          <p className="proof-stat">500+ member organizations across 30+ countries</p>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="benefits-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <a href="https://www.ipmi.org/membership" target="_blank" rel="noopener noreferrer" className="btn-primary-large">
            View Membership Options
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default MemberBenefits;
